import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { ReactElement, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { AiOutlineMinusCircle,AiOutlinePlusCircle } from 'react-icons/ai';
import { BsToggleOff,BsToggleOn } from 'react-icons/bs';
import { z } from 'zod';

import { Button } from '@/components/button/button';
import { Input } from '@/components/input/input';
import Modal from '@/components/modal/modal';
import { useModalKeyboard } from '@/hooks/useModalKeyboard';

import styles from './agentScaling.module.scss';

type SizeOption = 'Small' | 'Medium' | 'Large';

const SIZE_DESCRIPTIONS: Record<SizeOption, string> = {
  Small: 'For light workloads (like LLM proxies) with light or infrequent traffic levels \u2013 1/2 CPU, 512Mi memory',
  Medium: 'For moderate workloads (like multi-step operations) or moderate traffic levels \u2013 1 CPU, 768Mi memory',
  Large: 'For compute\u2011intensive workloads (like data processing or complex tasks) or heavy traffic \u2013 2 CPU, 2Gi memory'
};

const scalingSchema = z.object( {
  autoScaling: z.boolean(),
  instances: z.number().int().min( 0 ),
  minInstances: z.number().int().min( 1 ),
  maxInstances: z.number().int().min( 2 ),
  size: z.enum( [ 'Small', 'Medium', 'Large' ] ),
  cpuUtilizationEnabled: z.boolean(),
  targetCpuUtilization: z.number().int().min( 1 ).max( 100 ),
  memoryUtilizationEnabled: z.boolean(),
  targetMemoryUtilization: z.number().int().min( 1 ).max( 100 )
} ).refine(
  data => !data.autoScaling || data.minInstances < data.maxInstances,
  { path: [ 'minInstances' ], message: 'Minimum must be less than maximum' }
);

type FormValues = z.infer<typeof scalingSchema>;

export type AgentScalingConfig = {
  autoScaling: boolean;
  instances: number;
  minInstances: number;
  maxInstances: number;
  size: SizeOption;
  targetCpuUtilization?: number;
  targetMemoryUtilization?: number;
};

type Props = {
  initialConfig?: Partial<FormValues>;
  maxInstanceLimit?: number;
  onApply: ( config: AgentScalingConfig ) => void;
  onCancel: () => void;
};

const DEFAULT_CONFIG: FormValues = {
  autoScaling: false,
  instances: 0,
  minInstances: 1,
  maxInstances: 2,
  size: 'Small',
  cpuUtilizationEnabled: true,
  targetCpuUtilization: 75,
  memoryUtilizationEnabled: false,
  targetMemoryUtilization: 75
};

const getCpuWarning = ( value: number ): string | undefined => {
  if ( value < 50 ) return 'Values below 50 may lead to aggressive scaling behavior. Consider setting to 50 for high availability, or 70 for cost optimization.';
  if ( value > 80 ) return 'Values above 80 may not leave room for traffic spikes.';
  return undefined;
};

const getMemoryWarning = ( value: number ): string | undefined => {
  if ( value < 70 ) return 'Values below 70 may lead to aggressive scaling behavior. Consider setting to a value between 70 and 80.';
  if ( value > 80 ) return 'Values above 80 may not leave room for traffic spikes.';
  return undefined;
};

export function AgentScaling( props: Props ): ReactElement {
  const { initialConfig, maxInstanceLimit = 20, onApply, onCancel } = props;
  const [ sizeDropdownOpen, setSizeDropdownOpen ] = useState( false );

  const { control, handleSubmit, watch, setValue, getValues, formState: { isDirty } } = useForm<FormValues>( {
    resolver: zodResolver( scalingSchema ),
    defaultValues: { ...DEFAULT_CONFIG, ...initialConfig }
  } );

  const autoScaling = watch( 'autoScaling' );
  const cpuUtilizationEnabled = watch( 'cpuUtilizationEnabled' );
  const memoryUtilizationEnabled = watch( 'memoryUtilizationEnabled' );
  const targetCpuUtilization = watch( 'targetCpuUtilization' );
  const targetMemoryUtilization = watch( 'targetMemoryUtilization' );

  const neitherUtilizationEnabled = autoScaling && !cpuUtilizationEnabled && !memoryUtilizationEnabled;

  const onSubmit = ( data: FormValues ) => {
    onApply( {
      autoScaling: data.autoScaling,
      instances: data.instances,
      minInstances: data.minInstances,
      maxInstances: data.maxInstances,
      size: data.size,
      targetCpuUtilization: data.cpuUtilizationEnabled ? data.targetCpuUtilization : undefined,
      targetMemoryUtilization: data.memoryUtilizationEnabled ? data.targetMemoryUtilization : undefined
    } );
  };

  useModalKeyboard( { onEscape: onCancel, onEnter: handleSubmit( onSubmit ), enterDisabled: !isDirty || !!neitherUtilizationEnabled } );

  return (
    <Modal className={ styles.scalingModal }>
      <h2 className={ styles.title }>Set desired agent capacity</h2>

      <p className={ styles.description }>
        Agent capacity controls how many instances are running to handle requests. More instances improve throughput and resilience.
      </p>

      <Controller
        control={ control }
        name="autoScaling"
        render={ ( { field } ) => (
          <div className={ styles.field }>
            <span className={ styles.label }>Auto-scaling:</span>
            <button
              className={ styles.toggle }
              onClick={ () => {
                if ( !field.value ) {
                  setValue( 'minInstances', Math.max( getValues( 'minInstances' ), 1 ) );
                  setValue( 'maxInstances', Math.max( getValues( 'maxInstances' ), 2 ) );
                }
                field.onChange( !field.value );
              } }
              aria-pressed={ field.value }
              type="button"
            >
              { field.value
                ? <BsToggleOn className={ styles.toggleOn } />
                : <BsToggleOff className={ styles.toggleOff } />}
            </button>
            <span className={ styles.toggleLabel }>{ field.value ? 'On' : 'Off' }</span>
            <span className={ styles.hint }>
              { field.value
                ? 'Dynamically scales based on use of CPU and memory'
                : 'Set a fixed number of agent instances'}
            </span>
          </div>
        ) }
      />

      { autoScaling ? (
        <>
          <Controller
            control={ control }
            name="minInstances"
            render={ ( { field } ) => (
              <div className={ styles.field }>
                <span className={ styles.label }>Minimum instances:</span>
                <div className={ styles.stepper }>
                  <button
                    type="button"
                    onClick={ () => field.onChange( Math.max( field.value - 1, 1 ) ) }
                    disabled={ field.value <= 1 }
                    aria-label="Decrease minimum instances"
                  >
                    <AiOutlineMinusCircle />
                  </button>
                  <span className={ styles.stepperValue }>{ field.value }</span>
                  <button
                    type="button"
                    onClick={ () => {
                      const next = field.value + 1;
                      field.onChange( next );
                      if ( next >= getValues( 'maxInstances' ) ) {
                        setValue( 'maxInstances', Math.min( next + 1, maxInstanceLimit ), { shouldDirty: true } );
                      }
                    } }
                    disabled={ field.value >= maxInstanceLimit - 1 }
                    aria-label="Increase minimum instances"
                  >
                    <AiOutlinePlusCircle />
                  </button>
                </div>
              </div>
            ) }
          />

          <Controller
            control={ control }
            name="maxInstances"
            render={ ( { field } ) => (
              <div className={ styles.field }>
                <span className={ styles.label }>Maximum instances:</span>
                <div className={ styles.stepper }>
                  <button
                    type="button"
                    onClick={ () => field.onChange( Math.max( field.value - 1, getValues( 'minInstances' ) + 1 ) ) }
                    disabled={ field.value <= getValues( 'minInstances' ) + 1 }
                    aria-label="Decrease maximum instances"
                  >
                    <AiOutlineMinusCircle />
                  </button>
                  <span className={ styles.stepperValue }>{ field.value }</span>
                  <button
                    type="button"
                    onClick={ () => field.onChange( Math.min( field.value + 1, maxInstanceLimit ) ) }
                    disabled={ field.value >= maxInstanceLimit }
                    aria-label="Increase maximum instances"
                  >
                    <AiOutlinePlusCircle />
                  </button>
                </div>
              </div>
            ) }
          />

          <div className={ styles.field }>
            <div className={ styles.utilizationLabel }>
              <button
                type="button"
                className={ styles.toggle }
                onClick={ () => {
                  const next = !cpuUtilizationEnabled;
                  setValue( 'cpuUtilizationEnabled', next, { shouldDirty: true } );
                  if ( next ) setValue( 'targetCpuUtilization', 75, { shouldDirty: true } );
                } }
                aria-pressed={ cpuUtilizationEnabled }
              >
                { cpuUtilizationEnabled
                  ? <BsToggleOn className={ styles.toggleOn } />
                  : <BsToggleOff className={ styles.toggleOff } /> }
              </button>
              <span className={ styles.label }>Target CPU utilization (%):</span>
            </div>
            <Controller
              control={ control }
              name="targetCpuUtilization"
              render={ ( { field } ) => (
                <Input
                  type="number"
                  min={ 1 }
                  max={ 100 }
                  disabled={ !cpuUtilizationEnabled }
                  value={ field.value }
                  onChange={ e => field.onChange( Math.min( 100, Math.max( 1, Number( e.target.value ) ) ) ) }
                  aria-label="Target CPU utilization"
                />
              ) }
            />
            { cpuUtilizationEnabled && getCpuWarning( targetCpuUtilization ) && (
              <p className={ styles.utilizationWarning }>{ getCpuWarning( targetCpuUtilization ) }</p>
            ) }
          </div>

          <div className={ styles.field }>
            <div className={ styles.utilizationLabel }>
              <button
                type="button"
                className={ styles.toggle }
                onClick={ () => {
                  const next = !memoryUtilizationEnabled;
                  setValue( 'memoryUtilizationEnabled', next, { shouldDirty: true } );
                  if ( next ) setValue( 'targetMemoryUtilization', 75, { shouldDirty: true } );
                } }
                aria-pressed={ memoryUtilizationEnabled }
              >
                { memoryUtilizationEnabled
                  ? <BsToggleOn className={ styles.toggleOn } />
                  : <BsToggleOff className={ styles.toggleOff } /> }
              </button>
              <span className={ styles.label }>Target memory utilization (%):</span>
            </div>
            <Controller
              control={ control }
              name="targetMemoryUtilization"
              render={ ( { field } ) => (
                <Input
                  type="number"
                  min={ 1 }
                  max={ 100 }
                  disabled={ !memoryUtilizationEnabled }
                  value={ field.value }
                  onChange={ e => field.onChange( Math.min( 100, Math.max( 1, Number( e.target.value ) ) ) ) }
                  aria-label="Target memory utilization"
                />
              ) }
            />
            { memoryUtilizationEnabled && getMemoryWarning( targetMemoryUtilization ) && (
              <p className={ styles.utilizationWarning }>{ getMemoryWarning( targetMemoryUtilization ) }</p>
            ) }
          </div>
        </>
      ) : (
        <Controller
          control={ control }
          name="instances"
          render={ ( { field } ) => (
            <div className={ styles.field }>
              <span className={ styles.label }>Instances:</span>
              <div className={ styles.stepper }>
                <button
                  type="button"
                  onClick={ () => field.onChange( Math.max( field.value - 1, 0 ) ) }
                  disabled={ field.value <= 0 }
                  aria-label="Decrease instances"
                >
                  <AiOutlineMinusCircle />
                </button>
                <span className={ styles.stepperValue }>{ field.value }</span>
                <button
                  type="button"
                  onClick={ () => field.onChange( Math.min( field.value + 1, maxInstanceLimit ) ) }
                  disabled={ field.value >= maxInstanceLimit }
                  aria-label="Increase instances"
                >
                  <AiOutlinePlusCircle />
                </button>
              </div>
            </div>
          ) }
        />
      ) }

      { neitherUtilizationEnabled && (
        <p className={ styles.utilizationError }>
          At least one of CPU or memory utilization must be enabled for effective auto-scaling.
        </p>
      ) }

      <Controller
        control={ control }
        name="size"
        render={ ( { field } ) => (
          <div className={ styles.field }>
            <span className={ styles.label }>Size:</span>
            <div className={ styles.sizeSelector }>
              <button
                type="button"
                className={ styles.sizeDropdown }
                onClick={ () => setSizeDropdownOpen( !sizeDropdownOpen ) }
              >
                <span>{ field.value }</span>
                <span className={ styles.sizeArrow }>&#9662;</span>
              </button>
              { sizeDropdownOpen && (
                <ul className={ styles.sizeOptions }>
                  { ( Object.keys( SIZE_DESCRIPTIONS ) as SizeOption[] ).map( option => (
                    <li key={ option }>
                      <button
                        type="button"
                        onClick={ () => { field.onChange( option ); setSizeDropdownOpen( false ); } }
                        className={ clsx( field.value === option && styles.selected ) }
                      >
                        { option }
                      </button>
                    </li>
                  ) ) }
                </ul>
              ) }
            </div>
            <span className={ styles.sizeDescription }>{ SIZE_DESCRIPTIONS[field.value] }</span>
          </div>
        ) }
      />

      <div className={ styles.actions }>
        <Button variant='secondary' size='sm' type='button' onClick={ onCancel }>Cancel</Button>
        <Button variant='primary' size='sm' type='button' disabled={ !isDirty || !!neitherUtilizationEnabled } onClick={ handleSubmit( onSubmit ) }>
          Apply Changes
        </Button>
      </div>
    </Modal>
  );
}
